// @flow

import * as React from 'react';
import { Box } from 'react-system';
import { Button, Divider } from '@material-ui/core';
import { subDays, startOfToday, isEqual } from 'date-fns';
import { useLocale } from '../../../hooks/locale';
import { useTheme } from '../../../theme';
import { createDescriptor } from '../../../controls/router';
import {
  pageDescriptor,
  onlineSinceFilterDescriptor,
} from './sharedDescriptors';

type Props = {|
  inline?: boolean,
  shouldFlush: boolean,
|};

const combinedFilterDescriptor = createDescriptor(
  onlineSinceFilterDescriptor,
  pageDescriptor,
);

export const OnlineSinceFilter = (props: Props): React.Node => {
  const { t } = useLocale();
  const { text, colors } = useTheme();

  const [
    configFilters,
    setConfigFilters,
    flushConfigFilters,
  ] = combinedFilterDescriptor.useState();

  const onlineSinceButtons: $ReadOnlyArray<{|
    filter: string,
    title: string,
    value: string,
  |}> = [
    {
      filter: subDays(startOfToday(), 1).toISOString(),
      title: t('yesterday'),
      value: 'Yesterday',
    },
    {
      filter: subDays(startOfToday(), 3).toISOString(),
      title: t('maxThreeDays'),
      value: 'Max. 3 days',
    },
    {
      filter: subDays(startOfToday(), 7).toISOString(),
      title: t('maxOneWeek'),
      value: 'Max. 1 week',
    },
    {
      filter: subDays(startOfToday(), 30).toISOString(),
      title: t('maxOneMonth'),
      value: 'Max. 1 month',
    },
  ];

  const getMinTimeIndex = () => {
    let minTimeIndex = 0;
    onlineSinceButtons.forEach((button, index) => {
      if (configFilters.createdAt_gte != null && button.filter != null) {
        if (
          isEqual(
            new Date(configFilters.createdAt_gte),
            new Date(button.filter),
          )
        ) {
          minTimeIndex = index;
        }
      }
    });
    return minTimeIndex;
  };

  const [activeFilter, setActiveFilter] = React.useState(
    configFilters.createdAt_gte != null
      ? onlineSinceButtons[getMinTimeIndex()].value
      : null,
  );

  const flush = () => {
    setConfigFilters({ page: 0 });
    if (props.shouldFlush) {
      flushConfigFilters();
    }
  };

  const submit = value => {
    setConfigFilters({
      createdAt_gte: value,
    });
    flush();
  };

  const isChecked = ({ value }) => {
    return value === activeFilter;
  };

  return (
    <>
      <Box pt={3} px={3} css={text.subtitle1}>
        {t('publishedSince')}
      </Box>
      <Box
        p={3}
        css={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gridGap: '12px 16px',
        }}
      >
        {onlineSinceButtons.map(button => (
          <Button
            key={button.title}
            css={{
              padding: '6px 10px',
              fontWeight: '600',
              borderColor: colors.grey300,
              '.MuiButton-label': text.ellipsis,
            }}
            onClick={() => {
              if (activeFilter == null || activeFilter !== button.value) {
                setActiveFilter(button.value);
                submit(button.filter);
              } else {
                setActiveFilter(null);
                submit(null);
              }
            }}
            variant={isChecked(button) ? 'contained' : 'outlined'}
            color={isChecked(button) ? 'primary' : 'inherit'}
          >
            {button.title}
          </Button>
        ))}
      </Box>
      <Divider css={{ margin: '6px 0' }} />
    </>
  );
};
